import { memo, useCallback } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import { Form } from 'src/shared/components/form/form.js';
import { MessageToast } from 'src/shared/components/message-toast/message-toast.js';
import { OpenGmailButton } from 'src/shared/components/open-gmail-button/open-gmail-button.js';
import { useShowMailSentToast } from 'src/shared/hooks/use-mail-sent.js';
import { useShowGmailButton } from 'src/shared/hooks/use-show-gmail-button.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';
import { redirectToSameUrlWithoutTab } from 'src/shared/utils/redirect-to-same-url-without-tab.js';

import { cnLoginVerifyEmail } from './login-verify-email.constants.js';

import './login-verify-email.css';

export interface LoginVerifyEmailProps {
    kcContext: KcContext.LoginVerifyEmail;
}

export const LoginVerifyEmail = memo<LoginVerifyEmailProps>(({ kcContext }) => {
    const { showMailSentToast, setShowMailSentToast, setWasMailSent } = useShowMailSentToast();

    const { url } = kcContext;
    const { msgStr } = useGetMsg(kcContext);
    const defaultEmail = msgStr('yourEmail');
    const userEmail = sessionStorage.getItem('userEmail') || sessionStorage.getItem('username') || defaultEmail;
    const showGmailButton = useShowGmailButton(userEmail || '');

    const handleResendClick = useCallback(() => {
        setWasMailSent('true');
    }, [setWasMailSent]);

    return (
        <Form action={url.loginAction} kcContext={kcContext} className={cnLoginVerifyEmail()}>
            <div className={cnLoginVerifyEmail('text-block')}>
                <span className={cnLoginVerifyEmail('title')}>{msgStr('emailSentMessageStart')} </span>
                <span className={cnLoginVerifyEmail('title', { email: true })}>{userEmail || msgStr('yourEmail')}</span>
                <span className={cnLoginVerifyEmail('title')}> {msgStr('emailSentMessageAccountSetup')}</span>
            </div>

            {showGmailButton && (
                <div className={cnLoginVerifyEmail('text-block')}>
                    <OpenGmailButton message={msgStr('openGmail')} />
                </div>
            )}

            <div className={cnLoginVerifyEmail('text-block')}>
                <span className={cnLoginVerifyEmail('note')}>{msgStr('emailVerifyInstruction2') + ' '}</span>
                <button
                    className={cnLoginVerifyEmail('action-text')}
                    type="submit"
                    value="resendMagicLink"
                    name="submitAction"
                    onClick={handleResendClick}
                >
                    <span className={cnLoginVerifyEmail('note', { accent: true })}>{msgStr('magicLinkResend')}</span>
                </button>
            </div>

            {showMailSentToast && (
                <MessageToast message={msgStr('emailResent')} onClose={() => setShowMailSentToast(false)} />
            )}

            <div
                className={cnLoginVerifyEmail('text-block', {
                    'without-margin': true,
                })}
            >
                <span
                    onClick={redirectToSameUrlWithoutTab}
                    className={cnLoginVerifyEmail('note', {
                        accent: true,
                    })}
                >
                    {msgStr('emailVerifyInstructionCancel')}
                </span>
            </div>
        </Form>
    );
});
